'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import { motion } from 'framer-motion';
import { Loader2 } from 'lucide-react';
import * as React from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import {
  SetupConnectFormActions,
  SetupConnectFormPanel,
} from '@/components/setup/experience/SetupConnectFormShell';
import { SetupConnectedPlatformPanel } from '@/components/setup/experience/SetupConnectedPlatformPanel';
import { SetupConnectStepSection } from '@/components/setup/experience/SetupConnectStepSection';
import { SetupShopifyOAuthGuidePanel } from '@/components/setup/experience/SetupShopifyOAuthGuidePanel';
import { SetupStepFooter } from '@/components/setup/experience/SetupStepFooter';
import { SETUP_FIELD_INPUT_CLASS, SETUP_PATH_MOTION } from '@/components/setup/experience/constants';
import { normalizeShopifyUrl } from '@/components/setup/experience/lib/setupConnectionUtils';
import type { SetupSessionStore } from '@/components/setup/experience/types';
import {
  createSetupShopifyOAuthSchema,
  type SetupShopifyOAuthFormValues,
} from '@/components/setup/setupIntegrationSchemas';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useGetAccountInfo } from '@/lib/api/account';
import { useDeleteIntegration, useIntegrationsList } from '@/lib/api/integrations';
import { trpc } from '@/lib/api/trpc';
import { buildShopifyOAuthAuthorizeUrl } from '@/lib/utils/shopify-oauth-utils';

interface SetupConnectShopifyStepProps {
  store: SetupSessionStore;
  orgSlug: string;
  /** Error message coming back from the OAuth callback (?oauth=error). */
  oauthError?: string | null;
  /** Already connected (e.g. back-navigated from shipping) — advance forward again. */
  onContinue: () => void;
  onBack: () => void;
}

export function SetupConnectShopifyStep({
  store,
  orgSlug,
  oauthError,
  onContinue,
  onBack,
}: SetupConnectShopifyStepProps) {
  const { t } = useTranslation(['setup', 'integrations']);
  const { data: accountInfo } = useGetAccountInfo();
  const [isRedirecting, setIsRedirecting] = React.useState(false);
  const [sessionError, setSessionError] = React.useState<string | null>(null);
  const connectSession = trpc.integrations.createShopifyConnectSession.useMutation();

  // A Shopify integration may already exist — e.g. the merchant connected, advanced to
  // shipping, then stepped back. Surface the connected state with a disconnect action instead
  // of an empty form, mirroring the integrations section.
  const { data: integrations, isLoading: isLoadingIntegrations } = useIntegrationsList(store.id);
  const deleteIntegration = useDeleteIntegration();
  const shopifyIntegration = React.useMemo(
    () =>
      integrations?.find((integration) => integration.provider === 'shopify' && integration.enabled) ?? null,
    [integrations],
  );

  // The validation keys live in the 'integrations' namespace; `t` defaults to 'setup'.
  const schema = React.useMemo(() => createSetupShopifyOAuthSchema((key) => t(`integrations:${key}`)), [t]);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<SetupShopifyOAuthFormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      clientId: '',
      clientSecret: '',
      shopUrl: '',
    },
  });

  const onSubmit = (data: SetupShopifyOAuthFormValues) => {
    if (!accountInfo?.id) return;

    const shopUrl = normalizeShopifyUrl(data.shopUrl);
    const clientId = data.clientId.trim();
    const clientSecret = data.clientSecret.trim();

    setIsRedirecting(true);
    setSessionError(null);

    // The pair goes to the server in a POST; the URL Shopify sees carries only the session id.
    connectSession.mutate(
      {
        storeId: store.id,
        shop: shopUrl,
        clientId,
        clientSecret,
        origin: 'setup',
        orgSlug: orgSlug || undefined,
        returnOrigin: window.location.origin,
      },
      {
        onSuccess: ({ sessionId }) => {
          window.location.href = buildShopifyOAuthAuthorizeUrl(sessionId);
        },
        onError: (error) => {
          setIsRedirecting(false);
          setSessionError(error.message);
        },
      },
    );
  };

  const connectError = sessionError ?? oauthError;

  // Wait for the first integrations read before deciding which view to show — avoids flashing
  // the empty form when the store is in fact already connected.
  if (isLoadingIntegrations && !integrations) {
    return (
      <div className="flex min-h-[16rem] items-center justify-center">
        <Loader2 className="text-muted-foreground size-6 animate-spin" aria-hidden />
      </div>
    );
  }

  if (shopifyIntegration) {
    const shopName =
      (shopifyIntegration.metadata?.shopName as string | undefined) ||
      (shopifyIntegration.credentials?.shopUrl as string | undefined) ||
      store.name ||
      t('integrations:shopify.yourStore');
    const connectedViaOAuth = Boolean(shopifyIntegration.metadata?.connectedAt);

    return (
      <div className="flex flex-col">
        <motion.div
          className="flex w-full max-w-none flex-col gap-0"
          initial={SETUP_PATH_MOTION.actions.initial}
          animate={SETUP_PATH_MOTION.actions.animate}
          transition={{ ...SETUP_PATH_MOTION.actions.transition, delay: 0.05 }}
        >
          <SetupConnectStepSection
            title={t('setup:experience.connect.shopifyTitle')}
            description={t('setup:experience.connect.connected.subtitle', {
              platform: t('integrations:names.shopify'),
            })}
            platform={{ logoSrc: '/integrations/shopify-icon.svg', name: t('integrations:names.shopify') }}
            platformInTitle
            statusBadge={t('setup:experience.connect.connected.badge')}
            footer={
              <SetupStepFooter
                backLabel={t('setup:footer.back')}
                onBack={onBack}
                backDisabled={deleteIntegration.isPending}
                primaryLabel={t('setup:experience.connect.continue')}
                primaryType="button"
                onPrimary={onContinue}
                primaryDisabled={deleteIntegration.isPending}
              />
            }
          >
            <SetupConnectFormPanel>
              <SetupConnectedPlatformPanel
                platform="shopify"
                platformName={t('integrations:names.shopify')}
                shopName={shopName}
                connectedViaOAuth={connectedViaOAuth}
                disconnectConfirm={t('integrations:shopify.disconnectConfirm')}
                isDisconnecting={deleteIntegration.isPending}
                onDisconnect={() => deleteIntegration.mutate({ integrationId: shopifyIntegration.id })}
              />
            </SetupConnectFormPanel>
          </SetupConnectStepSection>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="flex flex-col">
      <motion.form
        className="flex w-full max-w-none flex-col gap-0"
        onSubmit={handleSubmit(onSubmit)}
        noValidate
        initial={SETUP_PATH_MOTION.actions.initial}
        animate={SETUP_PATH_MOTION.actions.animate}
        transition={{ ...SETUP_PATH_MOTION.actions.transition, delay: 0.05 }}
      >
        <SetupConnectStepSection
          title={t('setup:experience.connect.shopifyTitle')}
          description={t('setup:experience.connect.shopifySubtitle')}
          platform={{ logoSrc: '/integrations/shopify-icon.svg', name: t('integrations:names.shopify') }}
          platformInTitle
          footer={
            <SetupConnectFormActions
              backLabel={t('setup:footer.back')}
              onBack={onBack}
              submitLabel={t('integrations:shopify.connectWithShopify')}
              isSubmitting={isRedirecting}
              submitDisabled={!accountInfo?.id}
            />
          }
        >
          <SetupConnectFormPanel>
            <div className="grid grid-cols-1 items-start gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,28rem)] lg:gap-8">
              <div className="flex flex-col gap-[0.875rem]">
                <div className="flex flex-col gap-2">
                  <Label
                    htmlFor="setup-xp-shopify-client-id"
                    className="text-muted-foreground text-sm font-medium leading-[1.3]"
                  >
                    {t('integrations:shopify.clientIdLabel')}
                  </Label>
                  <Input
                    id="setup-xp-shopify-client-id"
                    className={SETUP_FIELD_INPUT_CLASS}
                    placeholder={t('integrations:shopify.clientIdPlaceholder')}
                    autoComplete="off"
                    aria-invalid={Boolean(errors.clientId)}
                    {...register('clientId')}
                  />
                  {errors.clientId ? (
                    <p className="text-destructive m-0 text-xs">{errors.clientId.message}</p>
                  ) : null}
                </div>

                <div className="flex flex-col gap-2">
                  <Label
                    htmlFor="setup-xp-shopify-client-secret"
                    className="text-muted-foreground text-sm font-medium leading-[1.3]"
                  >
                    {t('integrations:shopify.clientSecretLabel')}
                  </Label>
                  <Input
                    id="setup-xp-shopify-client-secret"
                    className={SETUP_FIELD_INPUT_CLASS}
                    type="password"
                    placeholder={t('integrations:shopify.clientSecretPlaceholder')}
                    autoComplete="new-password"
                    aria-invalid={Boolean(errors.clientSecret)}
                    {...register('clientSecret')}
                  />
                  {errors.clientSecret ? (
                    <p className="text-destructive m-0 text-xs">{errors.clientSecret.message}</p>
                  ) : null}
                </div>

                <div className="flex flex-col gap-2">
                  <Label
                    htmlFor="setup-xp-shopify-url"
                    className="text-muted-foreground text-sm font-medium leading-[1.3]"
                  >
                    {t('integrations:shopify.storeUrlLabel')}
                  </Label>
                  <Input
                    id="setup-xp-shopify-url"
                    className={SETUP_FIELD_INPUT_CLASS}
                    placeholder={t('integrations:shopify.storeUrlPlaceholder')}
                    autoComplete="off"
                    aria-invalid={Boolean(errors.shopUrl)}
                    {...register('shopUrl')}
                  />
                  {errors.shopUrl ? (
                    <p className="text-destructive m-0 text-xs">{errors.shopUrl.message}</p>
                  ) : null}
                </div>

                {connectError ? (
                  <p className="text-destructive m-0 rounded-md bg-[color-mix(in_srgb,var(--destructive)_8%,var(--background))] px-3 py-2.5 text-xs">
                    {connectError}
                  </p>
                ) : null}
              </div>

              <SetupShopifyOAuthGuidePanel />
            </div>
          </SetupConnectFormPanel>
        </SetupConnectStepSection>
      </motion.form>
    </div>
  );
}
